import api from './client';

export interface CallPreferences {
  status: 'available' | 'offline';
  gender: string | null;
  seekGender: 'M' | 'F' | 'Mx' | 'everyone';
  interests: string[];
  mood: string | null;
  language: string;
  isAdultVerified: boolean;
}

export interface CallPreferencesResponse {
  status: string;
  gender: string | null;
  seek_gender: string;
  interests: string[];
  mood: string | null;
  language: string;
  is_adult_verified: boolean;
}

export interface OnlineCountResponse {
  online_count: number;
}

export interface JoinPoolResponse {
  online_count: number;
}

// Transform snake_case to camelCase
export function transformCallPreferences(data: CallPreferencesResponse): CallPreferences {
  return {
    status: data.status as 'available' | 'offline',
    gender: data.gender,
    seekGender: data.seek_gender as 'M' | 'F' | 'Mx' | 'everyone',
    interests: data.interests || [],
    mood: data.mood,
    language: data.language || 'any',
    isAdultVerified: data.is_adult_verified || false,
  };
}

export async function getCallPreferences(): Promise<CallPreferences> {
  const { data } = await api.get<CallPreferencesResponse>('/calls/preferences');
  return transformCallPreferences(data);
}

export async function updateCallPreferences(prefs: Partial<{
  status: string;
  gender: string | null;
  seekGender: string;
  interests: string[];
  mood: string | null;
  language: string;
}>): Promise<CallPreferences> {
  const { data } = await api.put<CallPreferencesResponse>('/calls/preferences', {
    status: prefs.status,
    gender: prefs.gender,
    seek_gender: prefs.seekGender,
    interests: prefs.interests,
    mood: prefs.mood,
    language: prefs.language,
  });
  return transformCallPreferences(data);
}

export async function verifyCallAge(birthDate: string): Promise<{ isAdultVerified: boolean }> {
  const { data } = await api.post<{ is_adult_verified: boolean }>('/calls/verify-age', {
    birth_date: birthDate,
    confirmed: true,
  });
  return { isAdultVerified: data.is_adult_verified };
}

export async function joinCallPool(): Promise<{ onlineCount: number }> {
  const { data } = await api.post<JoinPoolResponse>('/calls/pool');
  return { onlineCount: data.online_count };
}

export async function leaveCallPool(): Promise<void> {
  await api.delete('/calls/pool');
}

export async function getOnlineCount(): Promise<{ onlineCount: number }> {
  const { data } = await api.get<OnlineCountResponse>('/calls/online');
  return { onlineCount: data.online_count };
}

export async function blockCallUser(sessionId: string): Promise<void> {
  await api.post('/calls/block', { session_id: sessionId });
}

export async function reportCall(sessionId: string, reason: string, details?: string): Promise<void> {
  await api.post('/calls/report', {
    session_id: sessionId,
    reason,
    details,
  });
}

export async function getTurnCredentials(): Promise<RTCIceServer[]> {
  const { data } = await api.get<{ iceServers: RTCIceServer[] }>('/calls/turn-credentials');
  return data.iceServers;
}

// WebSocket signal URL
export function getCallSignalUrl(): string {
  const token = localStorage.getItem('access_token');
  const baseUrl = (import.meta.env.VITE_API_URL || 'https://api.blahblah.chat/api/v1')
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  return `${baseUrl}/calls/signal?token=${token}`;
}
